import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CloudLightning, Droplets, Wrench, Bug, Leaf, AlertTriangle,
  Plus, X, CheckCircle, TrendingDown, TrendingUp, Activity,
  Filter,
} from 'lucide-react'
import { api } from '../lib/api'

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  red:       '#F87171',
  blue:      '#60A5FA',
  purple:    '#A78BFA',
  teal:      '#2DD4BF',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

// ── Types ─────────────────────────────────────────────────────────────────────

type EventType = 'weather' | 'irrigation' | 'equipment' | 'pest' | 'beneficial' | 'disease' | 'other'
type Severity  = 'low' | 'medium' | 'high' | 'critical'

interface YieldImpactEvent {
  id: string
  event_type: EventType
  severity: Severity
  title: string
  description: string
  occurred_at: string
  resolved_at?: string
  resolution_notes: string
  affected_plant_ids: string[]
  affected_zone_ids: string[]
  estimated_impact_pct?: number
  estimated_impact_kg?: number
  ai_assessment?: string
  ai_confidence?: number
  tags: string[]
  created_by_user_id?: string
  created_at: string
  updated_at: string
}

interface ImpactSummary {
  total_events: number
  unresolved_events: number
  total_loss_kg: number
  total_gain_kg: number
  net_impact_kg: number
  by_type: Record<string, number>
  by_severity: Record<string, number>
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const EVENT_TYPES: { value: EventType; label: string; icon: typeof CloudLightning; color: string }[] = [
  { value: 'weather',    label: 'Weather',    icon: CloudLightning, color: C.blue },
  { value: 'irrigation', label: 'Irrigation', icon: Droplets,       color: C.teal },
  { value: 'equipment',  label: 'Equipment',  icon: Wrench,         color: C.amber },
  { value: 'pest',       label: 'Pest',       icon: Bug,            color: C.red },
  { value: 'beneficial', label: 'Beneficial', icon: Leaf,           color: C.green },
  { value: 'disease',    label: 'Disease',    icon: AlertTriangle,  color: C.purple },
  { value: 'other',      label: 'Other',      icon: Activity,       color: C.textMuted },
]

const SEVERITY_COLORS: Record<Severity, string> = {
  low:      C.green,
  medium:   C.amber,
  high:     '#FB923C',
  critical: C.red,
}

const SEVERITY_BG: Record<Severity, string> = {
  low:      'rgba(76,175,80,0.12)',
  medium:   'rgba(245,166,35,0.12)',
  high:     'rgba(251,146,60,0.12)',
  critical: 'rgba(248,113,113,0.12)',
}

function typeInfo(type: EventType) {
  return EVENT_TYPES.find(t => t.value === type) ?? EVENT_TYPES[EVENT_TYPES.length - 1]
}

function fmtImpact(pct?: number, kg?: number) {
  const parts: string[] = []
  if (pct != null) {
    const sign = pct >= 0 ? '+' : ''
    parts.push(`${sign}${pct.toFixed(1)}%`)
  }
  if (kg != null) {
    const sign = kg >= 0 ? '+' : ''
    parts.push(`${sign}${kg.toFixed(1)} kg`)
  }
  return parts.join(' / ') || '—'
}

function today() {
  return new Date().toISOString().slice(0, 10)
}

// ── API calls ─────────────────────────────────────────────────────────────────

const yieldImpactApi = {
  list: (params: Record<string, string>) =>
    api.get<YieldImpactEvent[]>('/yield-impact', { params }).then(r => r.data),
  summary: (params: Record<string, string>) =>
    api.get<ImpactSummary>('/yield-impact/summary', { params }).then(r => r.data),
  create: (data: Partial<YieldImpactEvent>) =>
    api.post<YieldImpactEvent>('/yield-impact', data).then(r => r.data),
  update: (id: string, data: Partial<YieldImpactEvent>) =>
    api.patch<YieldImpactEvent>(`/yield-impact/${id}`, data).then(r => r.data),
  remove: (id: string) => api.delete(`/yield-impact/${id}`),
  resolve: (id: string, resolved_at: string, notes: string) =>
    api.post<YieldImpactEvent>(`/yield-impact/${id}/resolve`, { resolved_at, notes }).then(r => r.data),
}

// ── Blank form ────────────────────────────────────────────────────────────────

const BLANK = {
  event_type:           'weather' as EventType,
  severity:             'medium' as Severity,
  title:                '',
  description:          '',
  occurred_at:          today(),
  resolved_at:          '',
  resolution_notes:     '',
  estimated_impact_pct: '',
  estimated_impact_kg:  '',
  tags:                 '',
}

// ── Create / Edit Modal ───────────────────────────────────────────────────────

function EventModal({
  existing,
  onClose,
}: {
  existing?: YieldImpactEvent | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    ...BLANK,
    ...(existing ? {
      event_type:           existing.event_type,
      severity:             existing.severity,
      title:                existing.title,
      description:          existing.description,
      occurred_at:          existing.occurred_at,
      resolved_at:          existing.resolved_at ?? '',
      resolution_notes:     existing.resolution_notes,
      estimated_impact_pct: existing.estimated_impact_pct != null ? String(existing.estimated_impact_pct) : '',
      estimated_impact_kg:  existing.estimated_impact_kg  != null ? String(existing.estimated_impact_kg)  : '',
      tags:                 (existing.tags ?? []).join(', '),
    } : {}),
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        event_type:       form.event_type,
        severity:         form.severity,
        title:            form.title.trim(),
        description:      form.description,
        occurred_at:      form.occurred_at,
        resolution_notes: form.resolution_notes,
        tags:             form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      }
      if (form.resolved_at) payload.resolved_at = form.resolved_at
      if (form.estimated_impact_pct !== '') payload.estimated_impact_pct = parseFloat(form.estimated_impact_pct)
      if (form.estimated_impact_kg  !== '') payload.estimated_impact_kg  = parseFloat(form.estimated_impact_kg)

      if (!payload.title) throw new Error('Title is required')

      if (existing) {
        return yieldImpactApi.update(existing.id, payload as Partial<YieldImpactEvent>)
      } else {
        return yieldImpactApi.create(payload as Partial<YieldImpactEvent>)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['yield-impact'] })
      qc.invalidateQueries({ queryKey: ['yield-impact-summary'] })
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  const inp: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: C.card2,
    border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
  }
  const lbl: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em',
  }
  const row: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: 540, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: C.text }}>
            {existing ? 'Edit impact event' : 'Log impact event'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, padding: '8px 12px', color: C.red, fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Type + Severity */}
          <div style={row}>
            <div>
              <label style={lbl}>Event type</label>
              <select value={form.event_type} onChange={e => set('event_type', e.target.value)} style={{ ...inp }}>
                {EVENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Severity</label>
              <select value={form.severity} onChange={e => set('severity', e.target.value)} style={{ ...inp }}>
                {(['low', 'medium', 'high', 'critical'] as Severity[]).map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label style={lbl}>Title *</label>
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g. Frost damage to citrus block"
              style={inp}
            />
          </div>

          {/* Description */}
          <div>
            <label style={lbl}>Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="What happened? What was affected?"
              rows={3}
              style={{ ...inp, resize: 'vertical' }}
            />
          </div>

          {/* Dates */}
          <div style={row}>
            <div>
              <label style={lbl}>Occurred on</label>
              <input type="date" value={form.occurred_at} onChange={e => set('occurred_at', e.target.value)} style={inp} />
            </div>
            <div>
              <label style={lbl}>Resolved on (optional)</label>
              <input type="date" value={form.resolved_at} onChange={e => set('resolved_at', e.target.value)} style={inp} />
            </div>
          </div>

          {/* Impact estimate */}
          <div style={row}>
            <div>
              <label style={lbl}>Est. impact %</label>
              <input
                type="number" step="0.1"
                value={form.estimated_impact_pct}
                onChange={e => set('estimated_impact_pct', e.target.value)}
                placeholder="-15 = 15% loss, +5 = 5% boost"
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Est. impact kg</label>
              <input
                type="number" step="0.1"
                value={form.estimated_impact_kg}
                onChange={e => set('estimated_impact_kg', e.target.value)}
                placeholder="-200 = 200 kg lost"
                style={inp}
              />
            </div>
          </div>

          {/* Resolution notes */}
          <div>
            <label style={lbl}>Resolution notes</label>
            <input
              value={form.resolution_notes}
              onChange={e => set('resolution_notes', e.target.value)}
              placeholder="How was it resolved?"
              style={inp}
            />
          </div>

          {/* Tags */}
          <div>
            <label style={lbl}>Tags (comma-separated)</label>
            <input
              value={form.tags}
              onChange={e => set('tags', e.target.value)}
              placeholder="frost, citrus, block-a"
              style={inp}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '9px 18px', background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            style={{ padding: '9px 18px', background: C.amber, border: 'none', borderRadius: 8, color: 'var(--color-bg)', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: save.isPending ? 0.6 : 1 }}
          >
            {save.isPending ? 'Saving…' : existing ? 'Update event' : 'Log event'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Resolve Modal ─────────────────────────────────────────────────────────────

function ResolveModal({ event, onClose }: { event: YieldImpactEvent; onClose: () => void }) {
  const qc = useQueryClient()
  const [resolvedAt, setResolvedAt] = useState(today())
  const [notes, setNotes] = useState('')

  const resolve = useMutation({
    mutationFn: () => yieldImpactApi.resolve(event.id, resolvedAt, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['yield-impact'] })
      qc.invalidateQueries({ queryKey: ['yield-impact-summary'] })
      onClose()
    },
  })

  const inp: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: C.card2,
    border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
  }
  const lbl: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em',
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: 400 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: C.text }}>Mark as resolved</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
            <X size={16} />
          </button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: C.textMuted }}>
          "{event.title}"
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>Resolution date</label>
            <input type="date" value={resolvedAt} onChange={e => setResolvedAt(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Resolution notes</label>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="What was done to resolve this?"
              rows={3} style={{ ...inp, resize: 'vertical' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={() => resolve.mutate()}
            disabled={resolve.isPending}
            style={{ padding: '8px 16px', background: C.green, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: resolve.isPending ? 0.6 : 1 }}
          >
            {resolve.isPending ? 'Resolving…' : 'Mark resolved'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Summary Cards ─────────────────────────────────────────────────────────────

function SummaryCards({ summary }: { summary?: ImpactSummary }) {
  const cards = [
    {
      label: 'Total events',
      value: summary?.total_events ?? 0,
      sub: `${summary?.unresolved_events ?? 0} unresolved`,
      icon: Activity,
      color: C.amber,
    },
    {
      label: 'Yield losses',
      value: summary ? `${summary.total_loss_kg.toFixed(1)} kg` : '—',
      sub: 'estimated total loss',
      icon: TrendingDown,
      color: C.red,
    },
    {
      label: 'Yield gains',
      value: summary ? `${summary.total_gain_kg.toFixed(1)} kg` : '—',
      sub: 'from beneficial events',
      icon: TrendingUp,
      color: C.green,
    },
    {
      label: 'Net impact',
      value: summary ? `${summary.net_impact_kg >= 0 ? '+' : ''}${summary.net_impact_kg.toFixed(1)} kg` : '—',
      sub: 'gain minus loss',
      icon: summary && summary.net_impact_kg >= 0 ? TrendingUp : TrendingDown,
      color: summary && summary.net_impact_kg >= 0 ? C.green : C.red,
    },
  ]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
      {cards.map(card => {
        const Icon = card.icon
        return (
          <div key={card.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Icon size={16} color={card.color} />
              <span style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {card.label}
              </span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 4 }}>{card.value}</div>
            <div style={{ fontSize: 12, color: C.textFaint }}>{card.sub}</div>
          </div>
        )
      })}
    </div>
  )
}

// ── Event Row ─────────────────────────────────────────────────────────────────

function EventRow({
  event,
  onEdit,
  onResolve,
  onDelete,
}: {
  event: YieldImpactEvent
  onEdit: () => void
  onResolve: () => void
  onDelete: () => void
}) {
  const info = typeInfo(event.event_type)
  const Icon = info.icon
  const isResolved = !!event.resolved_at

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '14px 18px', display: 'grid',
      gridTemplateColumns: '36px 1fr auto auto', gap: 14, alignItems: 'center',
      opacity: isResolved ? 0.65 : 1,
    }}>
      {/* Icon */}
      <div style={{ width: 36, height: 36, borderRadius: 8, background: `${info.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={17} color={info.color} />
      </div>

      {/* Content */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{event.title}</span>
          {/* Severity badge */}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100,
            background: SEVERITY_BG[event.severity], color: SEVERITY_COLORS[event.severity],
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {event.severity}
          </span>
          {/* Type badge */}
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100,
            background: `${info.color}18`, color: info.color,
          }}>
            {info.label}
          </span>
          {isResolved && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100,
              background: 'rgba(76,175,80,0.12)', color: C.green,
            }}>
              ✓ resolved
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12, color: C.textMuted, flexWrap: 'wrap' }}>
          <span>{event.occurred_at}</span>
          {(event.estimated_impact_pct != null || event.estimated_impact_kg != null) && (
            <span style={{ color: event.estimated_impact_kg != null && event.estimated_impact_kg < 0 ? C.red : C.green }}>
              {fmtImpact(event.estimated_impact_pct, event.estimated_impact_kg)}
            </span>
          )}
          {event.description && (
            <span style={{ color: C.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
              {event.description}
            </span>
          )}
        </div>
      </div>

      {/* Impact summary */}
      <div style={{ textAlign: 'right', minWidth: 80 }}>
        {event.estimated_impact_kg != null && (
          <div style={{ fontSize: 16, fontWeight: 700, color: event.estimated_impact_kg >= 0 ? C.green : C.red }}>
            {event.estimated_impact_kg >= 0 ? '+' : ''}{event.estimated_impact_kg.toFixed(1)} kg
          </div>
        )}
        {event.estimated_impact_pct != null && (
          <div style={{ fontSize: 11, color: C.textMuted }}>
            {event.estimated_impact_pct >= 0 ? '+' : ''}{event.estimated_impact_pct.toFixed(1)}%
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        {!isResolved && (
          <button
            onClick={onResolve}
            title="Mark resolved"
            style={{ background: 'rgba(76,175,80,0.12)', border: `1px solid rgba(76,175,80,0.25)`, borderRadius: 6, padding: '5px 10px', color: C.green, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
          >
            <CheckCircle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Resolve
          </button>
        )}
        <button
          onClick={onEdit}
          style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 10px', color: C.textMuted, cursor: 'pointer', fontSize: 12 }}
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 10px', color: C.textFaint, cursor: 'pointer', fontSize: 12 }}
        >
          <X size={12} />
        </button>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function YieldImpactPage() {
  const qc = useQueryClient()

  // Filters
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate]     = useState('')
  const [filterType, setFilterType]       = useState<EventType | ''>('')
  const [filterSeverity, setFilterSeverity] = useState<Severity | ''>('')
  const [unresolvedOnly, setUnresolvedOnly] = useState(false)

  // Modal state
  const [showCreate, setShowCreate]     = useState(false)
  const [editEvent, setEditEvent]       = useState<YieldImpactEvent | null>(null)
  const [resolveEvent, setResolveEvent] = useState<YieldImpactEvent | null>(null)

  // Build query params
  const params: Record<string, string> = {}
  if (fromDate) params.from = fromDate
  if (toDate)   params.to   = toDate
  if (filterType)     params.type     = filterType
  if (filterSeverity) params.severity = filterSeverity
  if (unresolvedOnly) params.unresolved = 'true'

  const { data: events, isLoading } = useQuery({
    queryKey: ['yield-impact', params],
    queryFn: () => yieldImpactApi.list(params),
  })

  const { data: summary } = useQuery({
    queryKey: ['yield-impact-summary', { from: params.from, to: params.to }],
    queryFn: () => yieldImpactApi.summary({ from: params.from ?? '', to: params.to ?? '' }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => yieldImpactApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['yield-impact'] })
      qc.invalidateQueries({ queryKey: ['yield-impact-summary'] })
    },
  })

  const sel: React.CSSProperties = {
    background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '7px 12px', color: C.text, fontSize: 13, outline: 'none',
  }

  return (
    <main id="main-content" style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>
            Yield Impact Events
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>
            Track events that affect your expected harvest — weather damage, pest outbreaks, beneficial conditions.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.amber, border: 'none', borderRadius: 10, padding: '10px 18px', color: 'var(--color-bg)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
        >
          <Plus size={16} />
          Log event
        </button>
      </div>

      {/* Summary cards */}
      <SummaryCards summary={summary} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
        <Filter size={14} color={C.textMuted} />

        <select value={filterType} onChange={e => setFilterType(e.target.value as EventType | '')} style={sel}>
          <option value="">All types</option>
          {EVENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value as Severity | '')} style={sel}>
          <option value="">All severities</option>
          {(['low', 'medium', 'high', 'critical'] as Severity[]).map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>

        <input
          type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          placeholder="From date"
          style={{ ...sel, color: fromDate ? C.text : C.textFaint }}
        />
        <span style={{ color: C.textFaint, fontSize: 12 }}>to</span>
        <input
          type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          style={{ ...sel, color: toDate ? C.text : C.textFaint }}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textMuted, cursor: 'pointer' }}>
          <input
            type="checkbox" checked={unresolvedOnly} onChange={e => setUnresolvedOnly(e.target.checked)}
            style={{ accentColor: C.amber }}
          />
          Unresolved only
        </label>

        {(fromDate || toDate || filterType || filterSeverity || unresolvedOnly) && (
          <button
            onClick={() => { setFromDate(''); setToDate(''); setFilterType(''); setFilterSeverity(''); setUnresolvedOnly(false) }}
            style={{ background: 'none', border: 'none', color: C.textFaint, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Events list */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.textFaint }}>Loading events…</div>
      ) : events && events.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {events.map(event => (
            <EventRow
              key={event.id}
              event={event}
              onEdit={() => setEditEvent(event)}
              onResolve={() => setResolveEvent(event)}
              onDelete={() => {
                if (window.confirm('Delete this event?')) remove.mutate(event.id)
              }}
            />
          ))}
        </div>
      ) : (
        <div style={{
          background: C.card, border: `1px dashed ${C.border}`, borderRadius: 14,
          padding: '48px 32px', textAlign: 'center',
        }}>
          <Activity size={36} color={C.textFaint} style={{ marginBottom: 12 }} />
          <p style={{ color: C.textMuted, margin: '0 0 8px', fontSize: 15 }}>No impact events logged</p>
          <p style={{ color: C.textFaint, fontSize: 13, margin: '0 0 20px' }}>
            Record weather damage, pest outbreaks, equipment failures, or beneficial events that affect your yield.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            style={{ background: C.amber, border: 'none', borderRadius: 8, padding: '9px 18px', color: 'var(--color-bg)', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
          >
            <Plus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Log first event
          </button>
        </div>
      )}

      {/* By-type breakdown if we have data */}
      {summary && summary.total_events > 0 && (
        <div style={{ marginTop: 28, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Breakdown
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            {EVENT_TYPES.filter(t => summary.by_type[t.value] > 0).map(t => {
              const Icon = t.icon
              return (
                <div key={t.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: C.card2, borderRadius: 8 }}>
                  <Icon size={14} color={t.color} />
                  <span style={{ fontSize: 12, color: C.textMuted }}>{t.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: C.text }}>
                    {summary.by_type[t.value]}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreate && <EventModal onClose={() => setShowCreate(false)} />}
      {editEvent   && <EventModal existing={editEvent} onClose={() => setEditEvent(null)} />}
      {resolveEvent && <ResolveModal event={resolveEvent} onClose={() => setResolveEvent(null)} />}
    </main>
  )
}
